import React, { useEffect, useRef, useState } from "react";
import { useAppSelector } from "../../redux/hooks";
import { RootState } from "../../redux/store";
import {
  useGetDefaultThemeQuery,
  useUploadThemeLogoMutation,
  useUpdateThemeMutation,
  IThemeMode,
  IGradientColors,
  IStrokeColors,
  IGradient,
  ThemeSettings,
  IColors,
} from "../../services/settings/setting.rtk.api";
import { ColorPicker } from "./ColorPicker";
import GradientColorPicker from "./GradientColorPicker";
import ScaledIframe, { ScaledIframeRef } from "./ScaledIframe";

import { Link, useNavigate, useParams } from "react-router-dom";

import { toast } from "../../core/toast";

const CreateTheme: React.FC = () => {
  const assetBaseUrl =
    import.meta.env.VITE_APP_ASSET_URL || "https://dev.assets.gymstream.app";
  const navigate = useNavigate();
  const { themeid } = useParams<{ themeid?: string }>();

  const [isDarkModeDefault, setIsDarkModeDefault] = useState(true);
  const [showOnTV, setShowOnTV] = useState(true);
  const [showOnMobile, setShowOnMobile] = useState(true);
  const [themeMode, setThemeMode] = useState<"dark" | "light">("dark");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [themeName, setThemeName] = useState("");
  const [brandName, setBrandName] = useState("GymStream");

  const [lightLogoUrl, setLightLogoUrl] = useState<string | null>(null);
  const [lightLogoId, setLightLogoId] = useState<string | null>(null);

  const [darkLogoUrl, setDarkLogoUrl] = useState<string | null>(null);
  const [darkLogoId, setDarkLogoId] = useState<string | null>(null);

  // This helps the UI know which one to show right now
  const currentLogoUrl = themeMode === "dark" ? darkLogoUrl : lightLogoUrl;
  const currentLogoId = themeMode === "dark" ? darkLogoId : lightLogoId;

  const logoInputRef = React.useRef<HTMLInputElement>(null);

  const [tvBannerUrl, setTvBannerUrl] = useState<string | null>(null);
  const [tvBannerId, setTvBannerId] = useState<string | null>(null);
  const tvBannerInputRef = React.useRef<HTMLInputElement>(null);

  const [mobileBannerUrl, setMobileBannerUrl] = useState<string | null>(null);
  const [mobileBannerId, setMobileBannerId] = useState<string | null>(null);
  const mobileBannerInputRef = React.useRef<HTMLInputElement>(null);
  const iframeRef = useRef<ScaledIframeRef>(null);

  // APIs
  const { data: defaultTheme, isLoading } = useGetDefaultThemeQuery();
  const [uploadThemeLogo, { isLoading: isUploadingLogo }] =
    useUploadThemeLogoMutation();
  const [updateTheme, { isLoading: isSavingTheme }] = useUpdateThemeMutation();
  // ==========================================
  // DARK MODE STATES
  // ==========================================
  const [darkColors, setDarkColors] = useState<IColors>({
    primary: "#C6D92C",
    secondary: "#2E2E2E",
    supporting: "#949494",
    icon: "#C6D92C",
    iconBackground: "#535B10",
    appBackground: "#232323",
    card: "#1F1F1F",
    popup: "#2A2A2A",
    headingText: "#F3F3F6",
    bodyText: "#F3F3F6",
    mutedText: "#535353",
    errorText: "#890002",
  });

  const [darkGradientColors, setDarkGradientColors] = useState<IGradientColors>(
    {
      icon: {
        from: { color: "#C6D92C", opacity: 90 },
        to: { color: "#EDF5C0", opacity: 90 },
      },
      iconBackground: {
        from: { color: "#C6D92C", opacity: 20 },
        to: { color: "#535B10", opacity: 20 },
      },
      deviceBackground: {
        from: { color: "#2E2E2E", opacity: 100 },
        to: { color: "#0F0F0F", opacity: 100 },
      },
      card: {
        from: { color: "#2E2E2E", opacity: 100 },
        to: { color: "#0F0F0F", opacity: 100 },
      },
      popup: {
        from: { color: "#2E2E2E", opacity: 100 },
        to: { color: "#0F0F0F", opacity: 100 },
      },
      progressBar: {
        from: { color: "#C6D92C", opacity: 100 },
        to: { color: "#535B10", opacity: 20 },
      },
      userBackground: {
        from: { color: "#2E2E2E", opacity: 100 },
        to: { color: "#0F0F0F", opacity: 100 },
      },
    },
  );

  const [darkStrokeColors, setDarkStrokeColors] = useState<IStrokeColors>({
    card: "#C6D92C",
    popup: "#C6D92C",
    progressBar: "#C6D92C",
  });

  // ==========================================
  // LIGHT MODE STATES
  // ==========================================
  const [lightColors, setLightColors] = useState<IColors>({
    primary: "#C6D92C",
    secondary: "#FFFFFF",
    supporting: "#949494",
    icon: "#2E2E2E",
    iconBackground: "#EDF5C0",
    appBackground: "#F8F8F8",
    card: "#FFFFFF",
    popup: "#FFFFFF",
    headingText: "#1A1A1A",
    bodyText: "#333333",
    mutedText: "#7A7A7A",
    errorText: "#D92D20",
  });

  const [lightGradientColors, setLightGradientColors] =
    useState<IGradientColors>({
      icon: {
        from: { color: "#C6D92C", opacity: 90 },
        to: { color: "#EDF5C0", opacity: 90 },
      },
      iconBackground: {
        from: { color: "#EDF5C0", opacity: 100 },
        to: { color: "#FFFFFF", opacity: 100 },
      },
      deviceBackground: {
        from: { color: "#FFFFFF", opacity: 100 },
        to: { color: "#F2F2F2", opacity: 100 },
      },
      card: {
        from: { color: "#FFFFFF", opacity: 100 },
        to: { color: "#F4F4F4", opacity: 100 },
      },
      popup: {
        from: { color: "#FFFFFF", opacity: 100 },
        to: { color: "#F4F4F4", opacity: 100 },
      },
      progressBar: {
        from: { color: "#C6D92C", opacity: 100 },
        to: { color: "#EDF5C0", opacity: 60 },
      },
      userBackground: {
        from: { color: "#FFFFFF", opacity: 100 },
        to: { color: "#F5F5F5", opacity: 100 },
      },
    });

  const [lightStrokeColors, setLightStrokeColors] = useState<IStrokeColors>({
    card: "#D9D9D9",
    popup: "#D9D9D9",
    progressBar: "#C6D92C",
  });

  const livePreviewUrl = React.useMemo(() => {
    const buildPreviewTheme = (
      colors: IColors,
      gradients: IGradientColors,
      strokes: IStrokeColors,
    ) => ({
      branding: {
        name: brandName || null,
        logo: currentLogoUrl || currentLogoId || null,
        visibility: {
          device: showOnTV,
          user: showOnMobile,
        },
      },
      colors,
      gradients: showAdvanced ? gradients : null,
      strokes: showAdvanced ? strokes : null,
      assets: {
        banners: {
          device: tvBannerUrl || tvBannerId || null,
          user: mobileBannerUrl || mobileBannerId || null,
        },
      },
    });

    const livePayload = {
      type: "CUSTOM",
      mode: themeMode === "dark" ? "DARK" : "LIGHT",
      defaultMode: isDarkModeDefault ? "DARK" : "LIGHT",
      lightTheme: buildPreviewTheme(
        lightColors,
        lightGradientColors,
        lightStrokeColors,
      ),
      darkTheme: buildPreviewTheme(
        darkColors,
        darkGradientColors,
        darkStrokeColors,
      ),
    };

    try {
      const encodedTheme = btoa(
        unescape(encodeURIComponent(JSON.stringify(livePayload))),
      );
      return `${import.meta.env.VITE_TV_APP_URL}/preview-theme?theme=${encodedTheme}`;
    } catch (e) {
      console.error("Base64 encoding failed for live preview", e);
      return `${import.meta.env.VITE_TV_APP_URL}/preview-theme`;
    }
  }, [
    themeMode,
    isDarkModeDefault,
    brandName,
    currentLogoId,
    currentLogoUrl,
    showOnTV,
    showOnMobile,
    showAdvanced,
    tvBannerUrl,
    tvBannerId,
    mobileBannerUrl,
    mobileBannerId,
    lightColors,
    lightGradientColors,
    lightStrokeColors,
    darkColors,
    darkGradientColors,
    darkStrokeColors,
  ]);

  const handleLogoUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      return toast.error("Only JPEG and PNG files are supported.");
    }
    if (file.size > 5 * 1024 * 1024) {
      return toast.error("File exceeds the 5MB limit.");
    }

    try {
      const response = await uploadThemeLogo({
        file,
        onUploadProgress: (progress) => {
          console.log("Upload Progress:", progress);
        },
      }).unwrap();

      const uploadedUrl = response?.result?.url || response?.url;
      const uploadedId = response?.result?._id || response?._id;

      const finalLogoUrl = `${assetBaseUrl}/files/app/themes/${response?.result?.key || response?.key}`;

      if (uploadedId) {
        if (themeMode === "dark") {
          setDarkLogoId(uploadedId);
          setDarkLogoUrl(finalLogoUrl);
        } else {
          setLightLogoId(uploadedId);
          setLightLogoUrl(finalLogoUrl);
        }
        toast.success("Logo uploaded successfully!");
      }
    } catch (error) {
      console.error("Logo upload failed:", error);
      toast.error("Failed to upload logo.");
    } finally {
      if (logoInputRef.current) logoInputRef.current.value = "";
    }
  };
  console.log(lightLogoUrl, darkLogoUrl, "lightLogoUrldarkLogoUrldarkLogoUrl");
  const handleTvBannerUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!["image/jpeg", "image/png"].includes(file.type)) {
      return toast.error("Only JPEG and PNG files are supported.");
    }
    if (file.size > 5 * 1024 * 1024) {
      return toast.error("File exceeds the 5MB limit.");
    }

    try {
      const response = await uploadThemeLogo({
        file,
        onUploadProgress: (progress) => {
          console.log("TV Banner Upload Progress:", progress);
        },
      }).unwrap();

      const uploadedUrl = `${assetBaseUrl}/files/app/themes/${response?.result?.key || response?.key}`;
      const uploadedId = response?.result?._id || response?._id;

      if (uploadedId) {
        setTvBannerId(uploadedId);
        setTvBannerUrl(uploadedUrl);
        toast.success("TV Banner uploaded successfully!");
      }
    } catch (error) {
      console.error("TV Banner upload failed:", error);
      toast.error("Failed to upload TV Banner.");
    } finally {
      if (tvBannerInputRef.current) tvBannerInputRef.current.value = "";
    }
  };

  const currentColors = themeMode === "dark" ? darkColors : lightColors;
  const currentGradients =
    themeMode === "dark" ? darkGradientColors : lightGradientColors;
  const currentStrokes =
    themeMode === "dark" ? darkStrokeColors : lightStrokeColors;

  const InfoIcon = () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="text-gray-500"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );

  const handleColorChange = (key: keyof IColors, value: string) => {
    if (themeMode === "dark") {
      setDarkColors((prev) => ({ ...prev, [key]: value }));
    } else {
      setLightColors((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handleGradientChange = (
    key: keyof IGradientColors,
    value: IGradient,
  ) => {
    if (themeMode === "dark") {
      setDarkGradientColors((prev) => ({ ...prev, [key]: value }));
    } else {
      setLightGradientColors((prev) => ({ ...prev, [key]: value }));
    }
  };

  const handleStrokeChange = (key: keyof IStrokeColors, value: string) => {
    if (themeMode === "dark") {
      setDarkStrokeColors((prev) => ({ ...prev, [key]: value }));
    } else {
      setLightStrokeColors((prev) => ({ ...prev, [key]: value }));
    }
  };

  const ToggleSwitch = ({
    checked,
    onChange,
  }: {
    checked: boolean;
    onChange: () => void;
  }) => (
    <button
      type="button"
      onClick={onChange}
      className="relative inline-flex h-6 w-12 items-center rounded-full border-2 border-[#2A2A2A] bg-[#0A0A0A] transition-colors focus:outline-none focus:ring-2 focus:ring-[#C4D82D] focus:ring-offset-2 focus:ring-offset-black"
      aria-checked={checked}
      role="switch"
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full transition-all duration-200 ease-in-out ${
          checked ? "translate-x-6 bg-[#C4D82D]" : "translate-x-0 bg-gray-500"
        }`}
      />
    </button>
  );

  const handleSaveTheme = async () => {
    if (!themeName.trim()) return;

    const buildThemePayload = (
      c: IColors,
      g: IGradientColors,
      s: IStrokeColors,
      targetLogoId: string | null,
    ): IThemeMode => ({
      branding: {
        name: brandName || null,
        logo: targetLogoId || null,
        visibility: {
          device: showOnTV,
          user: showOnMobile,
        },
      },
      colors: c,
      gradients: showAdvanced ? g : null,
      strokes: showAdvanced ? s : null,
      assets: {
        banners: {
          device: tvBannerId || null,
          user: mobileBannerId || null,
        },
      },
    });

    const payload: Partial<ThemeSettings> = {
      name: themeName,
      mode: themeMode === "dark" ? "DARK" : "LIGHT",
      defaultMode: isDarkModeDefault ? "DARK" : "LIGHT",
      lightTheme: buildThemePayload(
        lightColors,
        lightGradientColors,
        lightStrokeColors,
        lightLogoId,
      ),
      darkTheme: buildThemePayload(
        darkColors,
        darkGradientColors,
        darkStrokeColors,
        darkLogoId,
      ),
      status: "ACTIVE",
    };

    const targetId = themeid || defaultTheme?._id;

    if (!targetId) {
      toast.error("No theme ID found to update.");
      return;
    }

    try {
      console.log("Saving theme with payload:", payload);
      await updateTheme({ id: targetId, body: payload }).unwrap();
      toast.success("Theme settings saved successfully!");
      setShowSaveModal(false);
      navigate("/theme-settings");
    } catch (error) {
      console.error(`Failed to save theme:`, error);
      toast.error(`Failed to save theme`);
    }
  };

  type NavbarSliceState = { isOpen: boolean };
  const isSidebarOpen = useAppSelector(
    (state: RootState) => (state.navbar as NavbarSliceState).isOpen,
  );

  useEffect(() => {
    try {
      const sourceTheme = defaultTheme;
      if (!sourceTheme) return;

      setThemeName(sourceTheme.name || "");
      setThemeMode(sourceTheme.defaultMode === "DARK" ? "dark" : "light");
      setIsDarkModeDefault(sourceTheme.defaultMode === "DARK");

      const assetBaseUrl =
        import.meta.env.VITE_APP_ASSET_URL ||
        "https://dev.assets.gymstream.app";

      const lightBranding = sourceTheme.lightTheme?.branding as any;
      if (lightBranding?.name) {
        setBrandName(lightBranding.name);
      }
      const visibility = lightBranding?.visibility;
      if (visibility) {
        setShowOnTV(visibility.device ?? true);
        setShowOnMobile(visibility.user ?? true);
      }
      if (lightBranding?.logo && typeof lightBranding.logo === "object") {
        setLightLogoId(lightBranding.logo._id);
        setLightLogoUrl(
          `${assetBaseUrl}/files/app/themes/${lightBranding.logo.key}`,
        );
      } else if (typeof lightBranding.logo === "string") {
        setLightLogoId(lightBranding.logo);
      }

      const darkBranding = sourceTheme.darkTheme?.branding as any;
      if (darkBranding?.logo && typeof darkBranding.logo === "object") {
        setDarkLogoId(darkBranding.logo._id);
        setDarkLogoUrl(
          `${assetBaseUrl}/files/app/themes/${darkBranding.logo.key}`,
        );
      } else if (typeof darkBranding.logo === "string") {
        setDarkLogoId(darkBranding.logo);
      }

      const banners = sourceTheme.lightTheme?.assets?.banners as any;
      if (banners?.device && typeof banners.device === "object") {
        setTvBannerId(banners.device._id);
        setTvBannerUrl(
          `${assetBaseUrl}/files/app/themes/${banners.device.key}`,
        );
      } else if (typeof banners.device === "string") {
        setTvBannerId(banners.device);
      }

      if (banners?.user && typeof banners.user === "object") {
        setMobileBannerId(banners.user._id);
        setMobileBannerUrl(
          `${assetBaseUrl}/files/app/themes/${banners.user.key}`,
        );
      } else if (typeof banners.user === "string") {
        setMobileBannerId(banners.user);
      }

      // Populate DARK theme states
      const darkThemeData = sourceTheme.darkTheme;
      if (darkThemeData) {
        if (darkThemeData.colors)
          setDarkColors((prev) => ({ ...prev, ...darkThemeData.colors }));
        if (darkThemeData.gradients)
          setDarkGradientColors((prev) => ({
            ...prev,
            ...darkThemeData.gradients,
          }));
        if (darkThemeData.strokes)
          setDarkStrokeColors((prev) => ({
            ...prev,
            ...darkThemeData.strokes,
          }));
      }

      // Populate LIGHT theme states
      const lightThemeData = sourceTheme.lightTheme;
      if (lightThemeData) {
        if (lightThemeData.colors)
          setLightColors((prev) => ({ ...prev, ...lightThemeData.colors }));
        if (lightThemeData.gradients)
          setLightGradientColors((prev) => ({
            ...prev,
            ...lightThemeData.gradients,
          }));
        if (lightThemeData.strokes)
          setLightStrokeColors((prev) => ({
            ...prev,
            ...lightThemeData.strokes,
          }));
      }
    } catch (error) {
      console.error("Failed to load theme data.", error);
    }
  }, [defaultTheme]);

  const handleResetToDefault = () => {
    if (!defaultTheme) return;

    setThemeMode(defaultTheme.defaultMode === "DARK" ? "dark" : "light");
    setIsDarkModeDefault(defaultTheme.defaultMode === "DARK");

    const assetBaseUrl =
      import.meta.env.VITE_APP_ASSET_URL || "https://dev.assets.gymstream.app";

    const branding = defaultTheme.lightTheme?.branding as any;
    if (branding?.name) {
      setBrandName(branding.name);
    }
    const visibility = branding?.visibility;
    if (visibility) {
      setShowOnTV(visibility.device ?? true);
      setShowOnMobile(visibility.user ?? true);
    }
    if (branding?.logo && typeof branding.logo === "object") {
      setLightLogoId(branding.logo._id);
      setLightLogoUrl(
        `${assetBaseUrl}/files/subscriber/themes/${branding.logo.key}`,
      );
    } else if (typeof branding.logo === "string") {
      setLightLogoId(branding.logo);
    }
    const darkBranding = defaultTheme.darkTheme?.branding as any;
    if (darkBranding?.logo && typeof darkBranding.logo === "object") {
      setDarkLogoId(darkBranding.logo._id);
      setDarkLogoUrl(
        `${assetBaseUrl}/files/app/themes/${darkBranding.logo.key}`,
      );
    } else if (typeof darkBranding.logo === "string") {
      setDarkLogoId(darkBranding.logo);
    }

    const banners = defaultTheme.lightTheme?.assets?.banners as any;
    if (banners?.device && typeof banners.device === "object") {
      setTvBannerId(banners.device._id);
      setTvBannerUrl(`${assetBaseUrl}/files/app/themes/${banners.device.key}`);
    } else if (typeof banners.device === "string") {
      setTvBannerId(banners.device);
    }

    if (banners?.user && typeof banners.user === "object") {
      setMobileBannerId(banners.user._id);
      setMobileBannerUrl(
        `${assetBaseUrl}/files/app/themes/${banners.user.key}`,
      );
    } else if (typeof banners.user === "string") {
      setMobileBannerId(banners.user);
    }

    const darkThemeData = defaultTheme.darkTheme;
    if (darkThemeData) {
      if (darkThemeData.colors) {
        setDarkColors((prev) => ({ ...prev, ...darkThemeData.colors }));
      }
      if (darkThemeData.gradients) {
        setDarkGradientColors((prev) => ({
          ...prev,
          ...darkThemeData.gradients,
        }));
      }
      if (darkThemeData.strokes) {
        setDarkStrokeColors((prev) => ({ ...prev, ...darkThemeData.strokes }));
      }
    }

    const lightThemeData = defaultTheme.lightTheme;
    if (lightThemeData) {
      if (lightThemeData.colors) {
        setLightColors((prev) => ({ ...prev, ...lightThemeData.colors }));
      }
      if (lightThemeData.gradients) {
        setLightGradientColors((prev) => ({
          ...prev,
          ...lightThemeData.gradients,
        }));
      }
      if (lightThemeData.strokes) {
        setLightStrokeColors((prev) => ({
          ...prev,
          ...lightThemeData.strokes,
        }));
      }
    }

    toast.success("Theme reset to default successfully");
  };

  const sidebarWidth = isSidebarOpen ? 256 : 64;

  if (isLoading) {
    return (
      <div
        style={{
          marginLeft: sidebarWidth,
          width: `calc(100% - ${sidebarWidth}px)`,
        }}
        className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center text-white"
      >
        <div className="w-12 h-12 border-4 border-[#2A2A2A] border-t-[#C6D92C] rounded-full animate-spin mb-4"></div>
        <p className="text-gray-400 font-medium">Loading Theme Settings...</p>
      </div>
    );
  }

  return (
    <div
      style={{
        marginLeft: sidebarWidth,
        width: `calc(100% - ${sidebarWidth}px)`,
      }}
      className="min-h-screen bg-[#0A0A0A] text-white p-6 pt-20 transition-all pb-24 font-sans"
    >
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-8"></div>

      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-semibold mb-1">Theme Customization</h1>

            <div>
              <div className="mb-6">
                <div className="text-sm flex items-center gap-2">
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="text-gray-400"
                  >
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                  </svg>
                  <Link
                    to="/"
                    className={` hover:text-[#B6CB24] transition-colors`}
                  >
                    Dashboard
                  </Link>
                  <span>&gt;</span>

                  <Link
                    to="/theme-settings"
                    className={` hover:text-[#B6CB24] transition-colors`}
                  >
                    <span className="hover:text-[#B6CB24] transition-colors">
                      Settings
                    </span>
                  </Link>
                  <span>&gt;</span>
                  <Link
                    to="/theme-settings"
                    className={` hover:text-[#B6CB24] transition-colors`}
                  >
                    <span className="text-[#B6CB24] font-normal">Create</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-400">
            Customize how your gym content appears on TV
          </p>
        </div>

        <hr className="w-full border-t border-[#2A2A2A] my-2" />

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pt-5">
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center bg-[#1A1A1A] border border-[#333] rounded-full p-1">
              <button
                onClick={() => setThemeMode("dark")}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold tracking-wide transition-all ${themeMode === "dark" ? "bg-[#2A2A2A] text-[#B6CB24] border border-[#B6CB24]/50" : "text-gray-400 hover:text-gray-200"}`}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                </svg>
                DARK MODE
              </button>
              <button
                onClick={() => setThemeMode("light")}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold tracking-wide transition-all ${themeMode === "light" ? "bg-[#2A2A2A] text-[#B6CB24] border border-[#B6CB24]/50" : "text-gray-400 hover:text-gray-200"}`}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="5"></circle>
                  <line x1="12" y1="1" x2="12" y2="3"></line>
                  <line x1="12" y1="21" x2="12" y2="23"></line>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line>
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line>
                  <line x1="1" y1="12" x2="3" y2="12"></line>
                  <line x1="21" y1="12" x2="23" y2="12"></line>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line>
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line>
                </svg>
                LIGHT MODE
              </button>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-white">
                Set Dark Mode as Default
              </span>
              <ToggleSwitch
                checked={isDarkModeDefault}
                onChange={() => setIsDarkModeDefault(!isDarkModeDefault)}
              />
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button onClick={handleResetToDefault}>
              <svg
                width="177"
                height="42"
                viewBox="0 0 177 42"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  width="177"
                  height="42"
                  rx="10"
                  fill="url(#paint0_radial_5894_3797)"
                  fill-opacity="0.8"
                />
                <rect
                  x="0.5"
                  y="0.5"
                  width="176"
                  height="41"
                  rx="9.5"
                  stroke="url(#paint1_linear_5894_3797)"
                  stroke-opacity="0.8"
                />
                <g clip-path="url(#clip0_5894_3797)">
                  <path
                    d="M19.3437 20.9976C19.9762 20.9976 20.4895 21.4726 20.5812 22.0976C21.1154 25.7135 24.2387 28.4976 28.0004 28.4976C32.5637 28.4976 36.1895 24.4001 35.3895 19.6901C35.012 17.4693 33.5995 15.5043 31.6204 14.4293C28.6987 12.8435 25.387 13.3851 23.137 15.3001L24.0787 16.2418C24.6037 16.7668 24.232 17.6643 23.4895 17.6643H19.6679C19.2079 17.6643 18.8345 17.291 18.8345 16.831V13.0093C18.8345 12.2668 19.732 11.8951 20.257 12.4201L21.367 13.5301C24.3345 10.9018 28.7687 10.1076 32.6987 12.171C35.4654 13.6235 37.4187 16.3601 37.8845 19.4501C38.8195 25.6476 34.0237 30.9976 28.0012 30.9976C22.9837 30.9976 18.817 27.2835 18.1079 22.4593C17.9954 21.6926 18.5687 20.9976 19.3437 20.9976Z"
                    fill="white"
                  />
                </g>
                <path
                  d="M53.0043 26V15.8182H56.8224C57.6046 15.8182 58.2609 15.9541 58.7912 16.2259C59.3248 16.4976 59.7275 16.8788 59.9993 17.3693C60.2744 17.8565 60.4119 18.425 60.4119 19.0746C60.4119 19.7275 60.2727 20.2943 59.9943 20.7749C59.7192 21.2521 59.3132 21.6217 58.7763 21.8835C58.2393 22.142 57.5798 22.2713 56.7976 22.2713H54.0781V20.7401H56.549C57.0064 20.7401 57.3809 20.6771 57.6726 20.5511C57.9643 20.4219 58.1797 20.2346 58.3189 19.9893C58.4614 19.7408 58.5327 19.4358 58.5327 19.0746C58.5327 18.7133 58.4614 18.4051 58.3189 18.1499C58.1764 17.8913 57.9593 17.6958 57.6676 17.5632C57.3759 17.4273 56.9998 17.3594 56.5391 17.3594H54.8487V26H53.0043ZM58.2642 21.3864L60.7848 26H58.7266L56.2507 21.3864H58.2642ZM65.3015 26.1491C64.5359 26.1491 63.8746 25.9901 63.3178 25.6719C62.7643 25.3504 62.3384 24.8963 62.0401 24.3097C61.7418 23.7197 61.5927 23.0253 61.5927 22.2266C61.5927 21.4411 61.7418 20.7517 62.0401 20.1584C62.3417 19.5618 62.7627 19.0978 63.3029 18.7663C63.8432 18.4316 64.4779 18.2642 65.207 18.2642C65.6777 18.2642 66.1218 18.3404 66.5394 18.4929C66.9603 18.642 67.3316 18.8741 67.6531 19.1889C67.9779 19.5038 68.2331 19.9048 68.4187 20.392C68.6043 20.8759 68.6971 21.4527 68.6971 22.1222V22.674H62.4379V21.4609H66.9719C66.9686 21.1162 66.8941 20.8097 66.7482 20.5412C66.6024 20.2694 66.3986 20.0556 66.1367 19.8999C65.8782 19.7441 65.5766 19.6662 65.2319 19.6662C64.864 19.6662 64.5408 19.7557 64.2624 19.9347C63.984 20.1103 63.7669 20.3423 63.6112 20.6307C63.4587 20.9157 63.3808 21.2289 63.3775 21.5703V22.6293C63.3775 23.0734 63.4587 23.4545 63.6211 23.7727C63.7835 24.0876 64.0105 24.3295 64.3022 24.4986C64.5939 24.6643 64.9353 24.7472 65.3263 24.7472C65.5882 24.7472 65.8252 24.7107 66.0373 24.6378C66.2494 24.5616 66.4334 24.4505 66.5891 24.3047C66.7449 24.1589 66.8626 23.9782 66.9421 23.7628L68.6225 23.9517C68.5165 24.3958 68.3143 24.7836 68.016 25.1151C67.721 25.4432 67.3432 25.6984 66.8825 25.8807C66.4218 26.0597 65.8948 26.1491 65.3015 26.1491ZM76.2551 20.3821L74.6145 20.5611C74.5681 20.3954 74.4869 20.2396 74.3709 20.0938C74.2582 19.9479 74.1058 19.8303 73.9135 19.7408C73.7213 19.6513 73.486 19.6065 73.2076 19.6065C72.833 19.6065 72.5182 19.6877 72.263 19.8501C72.0111 20.0125 71.8868 20.223 71.8901 20.4815C71.8868 20.7036 71.968 20.8842 72.1337 21.0234C72.3027 21.1626 72.5811 21.277 72.9689 21.3665L74.2715 21.6449C74.994 21.8007 75.531 22.0476 75.8823 22.3857C76.2369 22.7237 76.4159 23.1662 76.4192 23.7131C76.4159 24.1937 76.275 24.6179 75.9966 24.9858C75.7215 25.3504 75.3387 25.6354 74.8482 25.8409C74.3577 26.0464 73.7942 26.1491 73.1578 26.1491C72.2232 26.1491 71.4708 25.9536 70.9007 25.5625C70.3307 25.1681 69.9909 24.6196 69.8816 23.9169L71.6365 23.7479C71.7161 24.0926 71.8851 24.3527 72.1436 24.5284C72.4022 24.7041 72.7386 24.7919 73.1529 24.7919C73.5804 24.7919 73.9235 24.7041 74.182 24.5284C74.4438 24.3527 74.5748 24.1357 74.5748 23.8771C74.5748 23.6584 74.4902 23.4777 74.3212 23.3352C74.1555 23.1927 73.897 23.0833 73.5456 23.0071L72.2431 22.7337C71.5106 22.5812 70.9687 22.3243 70.6174 21.9631C70.266 21.5985 70.092 21.1378 70.0953 20.581C70.092 20.1103 70.2196 19.7027 70.4782 19.358C70.74 19.0099 71.1029 18.7415 71.5669 18.5526C72.0343 18.3603 72.5729 18.2642 73.1827 18.2642C74.0776 18.2642 74.7819 18.4548 75.2956 18.8359C75.8127 19.2171 76.1325 19.7325 76.2551 20.3821ZM81.2839 26.1491C80.5183 26.1491 79.8571 25.9901 79.3002 25.6719C78.7467 25.3504 78.3208 24.8963 78.0225 24.3097C77.7243 23.7197 77.5751 23.0253 77.5751 22.2266C77.5751 21.4411 77.7243 20.7517 78.0225 20.1584C78.3242 19.5618 78.7451 19.0978 79.2853 18.7663C79.8256 18.4316 80.4603 18.2642 81.1895 18.2642C81.6601 18.2642 82.1042 18.3404 82.5218 18.4929C82.9428 18.642 83.314 18.8741 83.6355 19.1889C83.9603 19.5038 84.2155 19.9048 84.4011 20.392C84.5867 20.8759 84.6795 21.4527 84.6795 22.1222V22.674H78.4203V21.4609H82.9544C82.9511 21.1162 82.8765 20.8097 82.7306 20.5412C82.5848 20.2694 82.381 20.0556 82.1191 19.8999C81.8606 19.7441 81.559 19.6662 81.2143 19.6662C80.8464 19.6662 80.5233 19.7557 80.2449 19.9347C79.9664 20.1103 79.7493 20.3423 79.5936 20.6307C79.4411 20.9157 79.3632 21.2289 79.3599 21.5703V22.6293C79.3599 23.0734 79.4411 23.4545 79.6035 23.7727C79.7659 24.0876 79.993 24.3295 80.2846 24.4986C80.5763 24.6643 80.9177 24.7472 81.3088 24.7472C81.5706 24.7472 81.8076 24.7107 82.0197 24.6378C82.2318 24.5616 82.4158 24.4505 82.5716 24.3047C82.7273 24.1589 82.845 23.9782 82.9245 23.7628L84.6049 23.9517C84.4989 24.3958 84.2967 24.7836 83.9984 25.1151C83.7034 25.4432 83.3256 25.6984 82.8649 25.8807C82.4042 26.0597 81.8772 26.1491 81.2839 26.1491ZM90.0153 18.3636V19.7557H85.6254V18.3636H90.0153ZM86.7092 16.5341H88.5089V23.7031C88.5089 23.9451 88.5453 24.1307 88.6183 24.2599C88.6945 24.3859 88.7939 24.4721 88.9165 24.5185C89.0392 24.5649 89.1751 24.5881 89.3242 24.5881C89.4369 24.5881 89.5397 24.5798 89.6325 24.5632C89.7286 24.5466 89.8015 24.5317 89.8512 24.5185L90.1545 25.9254C90.0584 25.9586 89.9208 25.995 89.7418 26.0348C89.5662 26.0746 89.3507 26.0978 89.0955 26.1044C88.6448 26.1177 88.2388 26.0497 87.8775 25.9006C87.5162 25.7481 87.2295 25.5128 87.0174 25.1946C86.8086 24.8764 86.7058 24.4787 86.7092 24.0014V16.5341ZM98.8063 18.3636V19.7557H94.4164V18.3636H98.8063ZM95.5002 16.5341H97.2999V23.7031C97.2999 23.9451 97.3364 24.1307 97.4093 24.2599C97.4855 24.3859 97.5849 24.4721 97.7076 24.5185C97.8302 24.5649 97.9661 24.5881 98.1152 24.5881C98.2279 24.5881 98.3307 24.5798 98.4235 24.5632C98.5196 24.5466 98.5925 24.5317 98.6422 24.5185L98.9455 25.9254C98.8494 25.9586 98.7118 25.995 98.5328 26.0348C98.3572 26.0746 98.1417 26.0978 97.8865 26.1044C97.4358 26.1177 97.0298 26.0497 96.6685 25.9006C96.3072 25.7481 96.0205 25.5128 95.8084 25.1946C95.5996 24.8764 95.4969 24.4787 95.5002 24.0014V16.5341ZM103.56 26.1491C102.815 26.1491 102.168 25.9851 101.621 25.657C101.075 25.3288 100.65 24.8698 100.349 24.2798C100.05 23.6899 99.9013 23.0005 99.9013 22.2116C99.9013 21.4228 100.05 20.7318 100.349 20.1385C100.65 19.5452 101.075 19.0845 101.621 18.7564C102.168 18.4283 102.815 18.2642 103.56 18.2642C104.306 18.2642 104.952 18.4283 105.499 18.7564C106.046 19.0845 106.469 19.5452 106.767 20.1385C107.069 20.7318 107.219 21.4228 107.219 22.2116C107.219 23.0005 107.069 23.6899 106.767 24.2798C106.469 24.8698 106.046 25.3288 105.499 25.657C104.952 25.9851 104.306 26.1491 103.56 26.1491ZM103.57 24.7074C103.975 24.7074 104.313 24.5964 104.585 24.3743C104.856 24.1489 105.058 23.8473 105.191 23.4695C105.327 23.0916 105.395 22.6707 105.395 22.2067C105.395 21.7393 105.327 21.3168 105.191 20.9389C105.058 20.5578 104.856 20.2545 104.585 20.0291C104.313 19.8037 103.975 19.6911 103.57 19.6911C103.156 19.6911 102.811 19.8037 102.536 20.0291C102.264 20.2545 102.061 20.5578 101.925 20.9389C101.792 21.3168 101.726 21.7393 101.726 22.2067C101.726 22.6707 101.792 23.0916 101.925 23.4695C102.061 23.8473 102.264 24.1489 102.536 24.3743C102.811 24.5964 103.156 24.7074 103.57 24.7074ZM115.749 26H112.299V15.8182H115.819C116.83 15.8182 117.698 16.022 118.424 16.4297C119.153 16.834 119.713 17.4157 120.105 18.1747C120.496 18.9337 120.691 19.8419 120.691 20.8991C120.691 21.9598 120.494 22.8712 120.1 23.6335C119.709 24.3958 119.143 24.9808 118.404 25.3885C117.669 25.7962 116.784 26 115.749 26ZM114.144 24.4041H115.66C116.369 24.4041 116.961 24.2749 117.435 24.0163C117.909 23.7545 118.265 23.3651 118.504 22.848C118.742 22.3277 118.862 21.678 118.862 20.8991C118.862 20.1203 118.742 19.474 118.504 18.9602C118.265 18.4432 117.912 18.0571 117.445 17.8018C116.981 17.5433 116.404 17.4141 115.715 17.4141H114.144V24.4041ZM125.745 26.1491C124.979 26.1491 124.318 25.9901 123.761 25.6719C123.208 25.3504 122.782 24.8963 122.483 24.3097C122.185 23.7197 122.036 23.0253 122.036 22.2266C122.036 21.4411 122.185 20.7517 122.483 20.1584C122.785 19.5618 123.206 19.0978 123.746 18.7663C124.287 18.4316 124.921 18.2642 125.65 18.2642C126.121 18.2642 126.565 18.3404 126.983 18.4929C127.404 18.642 127.775 18.8741 128.096 19.1889C128.421 19.5038 128.676 19.9048 128.862 20.392C129.048 20.8759 129.14 21.4527 129.14 22.1222V22.674H122.881V21.4609H127.415C127.412 21.1162 127.337 20.8097 127.192 20.5412C127.046 20.2694 126.842 20.0556 126.58 19.8999C126.322 19.7441 126.02 19.6662 125.675 19.6662C125.307 19.6662 124.984 19.7557 124.706 19.9347C124.427 20.1103 124.21 20.3423 124.055 20.6307C123.902 20.9157 123.824 21.2289 123.821 21.5703V22.6293C123.821 23.0734 123.902 23.4545 124.064 23.7727C124.227 24.0876 124.454 24.3295 124.746 24.4986C125.037 24.6643 125.379 24.7472 125.77 24.7472C126.032 24.7472 126.269 24.7107 126.481 24.6378C126.693 24.5616 126.877 24.4505 127.032 24.3047C127.188 24.1589 127.306 23.9782 127.385 23.7628L129.066 23.9517C128.96 24.3958 128.758 24.7836 128.459 25.1151C128.164 25.4432 127.787 25.6984 127.326 25.8807C126.865 26.0597 126.338 26.1491 125.745 26.1491ZM134.531 18.3636V19.7557H130.017V18.3636H134.531ZM131.145 26V17.6428C131.145 17.129 131.251 16.7015 131.463 16.3601C131.679 16.0187 131.967 15.7635 132.328 15.5945C132.69 15.4254 133.091 15.3409 133.532 15.3409C133.843 15.3409 134.12 15.3658 134.362 15.4155C134.604 15.4652 134.783 15.5099 134.899 15.5497L134.541 16.9418C134.465 16.9186 134.368 16.8954 134.252 16.8722C134.136 16.8456 134.007 16.8324 133.865 16.8324C133.53 16.8324 133.293 16.9136 133.154 17.076C133.018 17.2351 132.95 17.4638 132.95 17.7621V26H131.145ZM137.878 26.1541C137.394 26.1541 136.958 26.0679 136.57 25.8956C136.186 25.7199 135.881 25.4614 135.656 25.12C135.434 24.7786 135.323 24.3577 135.323 23.8572C135.323 23.4264 135.402 23.0701 135.561 22.7884C135.72 22.5066 135.937 22.2812 136.213 22.1122C136.488 21.9432 136.798 21.8156 137.142 21.7294C137.49 21.6399 137.85 21.5753 138.221 21.5355C138.669 21.4891 139.031 21.4477 139.31 21.4112C139.588 21.3714 139.79 21.3118 139.916 21.2322C140.046 21.1494 140.11 21.0218 140.11 20.8494V20.8196C140.11 20.4451 139.999 20.1551 139.777 19.9496C139.555 19.7441 139.235 19.6413 138.818 19.6413C138.377 19.6413 138.027 19.7375 137.769 19.9297C137.513 20.1219 137.341 20.349 137.252 20.6108L135.571 20.3722C135.704 19.9081 135.923 19.5204 136.227 19.2088C136.532 18.8939 136.905 18.6586 137.346 18.5028C137.787 18.3438 138.274 18.2642 138.808 18.2642C139.176 18.2642 139.542 18.3073 139.906 18.3935C140.271 18.4796 140.604 18.6222 140.906 18.821C141.207 19.0166 141.449 19.2834 141.632 19.6214C141.817 19.9595 141.91 20.3821 141.91 20.8892V26H140.18V24.951H140.12C140.011 25.1631 139.857 25.362 139.658 25.5476C139.462 25.7299 139.215 25.8774 138.917 25.9901C138.622 26.0994 138.276 26.1541 137.878 26.1541ZM138.345 24.8317C138.707 24.8317 139.02 24.7604 139.285 24.6179C139.55 24.4721 139.754 24.2798 139.896 24.0412C140.042 23.8026 140.115 23.5424 140.115 23.2607V22.3608C140.059 22.4072 139.963 22.4503 139.827 22.4901C139.694 22.5298 139.545 22.5646 139.379 22.5945C139.214 22.6243 139.05 22.6508 138.887 22.674C138.725 22.6972 138.584 22.7171 138.465 22.7337C138.196 22.7701 137.956 22.8298 137.744 22.9126C137.532 22.9955 137.364 23.1115 137.242 23.2607C137.119 23.4065 137.058 23.5954 137.058 23.8274C137.058 24.1589 137.179 24.4091 137.421 24.5781C137.663 24.7472 137.971 24.8317 138.345 24.8317ZM148.587 22.7884V18.3636H150.387V26H148.642V24.6428H148.562C148.39 25.0703 148.106 25.42 147.712 25.6918C147.321 25.9635 146.838 26.0994 146.265 26.0994C145.765 26.0994 145.322 25.9884 144.938 25.7663C144.557 25.541 144.258 25.2145 144.043 24.7869C143.827 24.3561 143.72 23.8357 143.72 23.2259V18.3636H145.519V22.9474C145.519 23.4313 145.652 23.8158 145.917 24.1009C146.182 24.3859 146.53 24.5284 146.961 24.5284C147.226 24.5284 147.483 24.4638 147.732 24.3345C147.98 24.2053 148.184 24.013 148.343 23.7578C148.506 23.4993 148.587 23.1761 148.587 22.7884ZM154.037 15.8182V26H152.237V15.8182H154.037ZM159.701 18.3636V19.7557H155.311V18.3636H159.701ZM156.395 16.5341H158.194V23.7031C158.194 23.9451 158.231 24.1307 158.304 24.2599C158.38 24.3859 158.479 24.4721 158.602 24.5185C158.725 24.5649 158.861 24.5881 159.01 24.5881C159.122 24.5881 159.225 24.5798 159.318 24.5632C159.414 24.5466 159.487 24.5317 159.537 24.5185L159.84 25.9254C159.744 25.9586 159.606 25.995 159.427 26.0348C159.252 26.0746 159.036 26.0978 158.781 26.1044C158.33 26.1177 157.924 26.0497 157.563 25.9006C157.202 25.7481 156.915 25.5128 156.703 25.1946C156.494 24.8764 156.391 24.4787 156.395 24.0014V16.5341Z"
                  fill="white"
                />
                <defs>
                  <radialGradient
                    id="paint0_radial_5894_3797"
                    cx="0"
                    cy="0"
                    r="1"
                    gradientUnits="userSpaceOnUse"
                    gradientTransform="translate(92.8812) rotate(90) scale(42 166.682)"
                  >
                    <stop stop-color="#2E2E2E" />
                    <stop offset="1" stop-color="#474747" />
                  </radialGradient>
                  <linearGradient
                    id="paint1_linear_5894_3797"
                    x1="88.5"
                    y1="0"
                    x2="88.5"
                    y2="42"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stop-color="#9B9DB6" />
                    <stop offset="0.216346" stop-color="#D6D7E1" />
                    <stop offset="0.528846" stop-color="#F3F3F6" />
                    <stop offset="0.831731" stop-color="#D6D7E1" />
                    <stop offset="1" stop-color="#9B9DB6" />
                  </linearGradient>
                  <clipPath id="clip0_5894_3797">
                    <rect
                      width="20"
                      height="20"
                      fill="white"
                      transform="translate(18 11)"
                    />
                  </clipPath>
                </defs>
              </svg>
            </button>

            <button
              style={{
                border: "1px solid var(--menu-active-border, #C6D92C)",
                background:
                  "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                padding: "8px 32px",
                borderRadius: "10px",
              }}
              onClick={() => {
                setShowSaveModal(true);
              }}
            >
              Save theme
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">
        {/* Left Column (Forms) */}
        <div className="xl:col-span-3 space-y-6">
          <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl py-4 px-6">
            <h2 className="text-lg font-semibold mb-2">Branding</h2>
            <hr className="border-t border-[#2A2A2A] my-2 -mx-6" />

            <div className="mb-6 md:w-1/2 md:pr-4">
              <label className="text-sm font-medium mb-2 block text-gray-300">
                Brand Name
              </label>
              <input
                type="text"
                value={brandName}
                onChange={(e) => setBrandName(e.target.value)}
                placeholder="Enter brand name"
                className="w-full bg-[#111] border border-[#333] rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-[#C6D92C] transition-colors"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6">
              <div>
                <div className="flex justify-between mb-2 ">
                  <label className="text-sm font-medium">Logo</label>
                </div>
                <p className="text-xs text-gray-500 mb-2">
                  Max file size is 5mb. Supported files are JPEG and PNG
                </p>

                <input
                  type="file"
                  ref={logoInputRef}
                  className="hidden"
                  accept="image/jpeg, image/png"
                  onChange={handleLogoUpload}
                />

                <div
                  onClick={() =>
                    !isUploadingLogo && logoInputRef.current?.click()
                  }
                  className={`border-2 border-dashed bg-[#181818] border-[#B6CB24]/50 rounded-xl p-8 flex flex-col items-center justify-center text-center transition-colors group ${
                    isUploadingLogo
                      ? "opacity-50 cursor-not-allowed"
                      : "cursor-pointer hover:bg-[#B6CB24]/5"
                  }`}
                >
                  {isUploadingLogo ? (
                    <p className="text-sm text-[#C6D92C] py-4">Uploading...</p>
                  ) : (
                    <>
                      <p className="text-sm text-gray-300">
                        Drag and drop file here
                      </p>
                      <p className="text-xs text-gray-500 my-1">or</p>
                      <p className="text-sm text-gray-300">
                        Click to browse files
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div>
                <p className="text-sm text-white font-medium block mb-2 text-transparent select-none">
                  Current Logo
                </p>
                <p className="text-xs text-transparent mb-2 select-none">
                  Spacer
                </p>
                <div className="bg-[#111] border border-[#333] rounded-xl h-[164px] flex items-center justify-center overflow-hidden p-2">
                  {currentLogoUrl ? (
                    <img
                      src={currentLogoUrl}
                      alt="Uploaded Logo"
                      className="max-w-full max-h-full object-contain rounded-lg"
                    />
                  ) : (
                    <div className="flex items-center gap-3"></div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl ">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-semibold">Colors</h2>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-gray-400"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-6 mb-2">
                <ColorPicker
                  label="Primary Color"
                  value={currentColors.primary ?? "#C6D92C"}
                  onChange={(v) => handleColorChange("primary", v)}
                />
                <ColorPicker
                  label="Secondary Color"
                  value={currentColors.secondary ?? "#FFFFFF"}
                  onChange={(v) => handleColorChange("secondary", v)}
                />
                <ColorPicker
                  label="Supporting Color"
                  value={currentColors.supporting ?? "#949494"}
                  onChange={(v) => handleColorChange("supporting", v)}
                />
                <div className="hidden md:block"></div>
                <ColorPicker
                  label="Icon Color"
                  value={currentColors.icon ?? "#C6D92C"}
                  onChange={(v) => handleColorChange("icon", v)}
                />
                <ColorPicker
                  label="Icon Background Color"
                  value={currentColors.iconBackground ?? "#2E2E2E"}
                  onChange={(v) => handleColorChange("iconBackground", v)}
                />
                <ColorPicker
                  label="App Background Color"
                  value={currentColors.appBackground ?? "#2E2E2E"}
                  onChange={(v) => handleColorChange("appBackground", v)}
                />
                <ColorPicker
                  label="Cards Color"
                  value={currentColors.card ?? "#2E2E2E"}
                  onChange={(v) => handleColorChange("card", v)}
                />
                <ColorPicker
                  label="Popups Color"
                  value={currentColors.popup ?? "#2E2E2E"}
                  onChange={(v) => handleColorChange("popup", v)}
                />
                <ColorPicker
                  label="Heading Text"
                  value={currentColors.headingText ?? "#FFFFFF"}
                  onChange={(v) => handleColorChange("headingText", v)}
                />
                <ColorPicker
                  label="Body Text"
                  value={currentColors.bodyText ?? "#F3F3F6"}
                  onChange={(v) => handleColorChange("bodyText", v)}
                />
                <ColorPicker
                  label="Muted Text"
                  value={currentColors.mutedText ?? "#535353"}
                  onChange={(v) => handleColorChange("mutedText", v)}
                />
                <ColorPicker
                  label="Error Text"
                  value={currentColors.errorText ?? "#890002"}
                  onChange={(v) => handleColorChange("errorText", v)}
                />
              </div>
            </div>

            {!showAdvanced && (
              <div className="p-6 pt-0">
                <div className="flex justify-center items-center gap-4 border-t border-[#2A2A2A] pt-6 mt-4">
                  <button
                    onClick={() => setShowAdvanced(true)}
                    className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors font-medium text-sm"
                  >
                    Advanced Settings
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {showAdvanced && (
              <div className="px-6 pb-6 pt-2">
                <div className="flex items-center gap-4 mb-6">
                  <hr className="flex-1 border-[#2A2A2A]" />
                  <button
                    onClick={() => setShowAdvanced(false)}
                    className="text-sm font-medium text-gray-300 hover:text-white transition-colors flex items-center gap-2"
                  >
                    Advanced Settings
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="18 15 12 9 6 15"></polyline>
                    </svg>
                  </button>
                  <hr className="flex-1 border-[#2A2A2A]" />
                </div>

                <h3 className="text-base font-semibold mb-4">
                  TV and Applications
                </h3>

                <div className="flex gap-x-8 mb-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 mb-2">
                      <label className="text-sm font-medium">Icons color</label>
                      <InfoIcon />
                    </div>
                    <div className="flex text-xs text-gray-400 mb-1">
                      <span className="flex-1">Color</span>
                      <span className="w-[80px]">Transparency</span>
                    </div>
                    <GradientColorPicker
                      defaultFrom={currentGradients.icon?.from}
                      defaultTo={currentGradients.icon?.to}
                      onChange={(val) => handleGradientChange("icon", val)}
                    />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 mb-2">
                      <label className="text-sm font-medium">
                        Icons background
                      </label>
                      <InfoIcon />
                    </div>
                    <div className="flex text-xs text-gray-400 mb-1">
                      <span className="flex-1">Color</span>
                      <span className="w-[80px]">Transparency</span>
                    </div>
                    <GradientColorPicker
                      defaultFrom={currentGradients.iconBackground?.from}
                      defaultTo={currentGradients.iconBackground?.to}
                      onChange={(val) =>
                        handleGradientChange("iconBackground", val)
                      }
                    />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 mb-2">
                      <label className="text-sm font-medium">Cards</label>
                      <InfoIcon />
                    </div>
                    <div className="flex text-xs text-gray-400 mb-1">
                      <span className="flex-1">Color</span>
                      <span className="w-[80px]">Transparency</span>
                    </div>
                    <GradientColorPicker
                      defaultFrom={currentGradients.card?.from}
                      defaultTo={currentGradients.card?.to}
                      onChange={(val) => handleGradientChange("card", val)}
                    />
                  </div>
                </div>

                <div className="flex gap-x-8 mb-6">
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 mb-2">
                      <label className="text-sm font-medium">Popup</label>
                      <InfoIcon />
                    </div>
                    <div className="flex text-xs text-gray-400 mb-1">
                      <span className="flex-1">Color</span>
                      <span className="w-[80px]">Transparency</span>
                    </div>
                    <GradientColorPicker
                      defaultFrom={currentGradients.popup?.from}
                      defaultTo={currentGradients.popup?.to}
                      onChange={(val) => handleGradientChange("popup", val)}
                    />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 mb-2">
                      <label className="text-sm font-medium">
                        Progress bar Gradient
                      </label>
                      <InfoIcon />
                    </div>
                    <div className="flex text-xs text-gray-400 mb-1">
                      <span className="flex-1">Color</span>
                      <span className="w-[80px]">Transparency</span>
                    </div>
                    <GradientColorPicker
                      defaultFrom={currentGradients.progressBar?.from}
                      defaultTo={currentGradients.progressBar?.to}
                      onChange={(val) =>
                        handleGradientChange("progressBar", val)
                      }
                    />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 mb-2">
                      <label className="text-sm font-medium">
                        TV Background
                      </label>
                      <InfoIcon />
                    </div>
                    <div className="flex text-xs text-gray-400 mb-1">
                      <span className="flex-1">Color</span>
                      <span className="w-[80px]">Transparency</span>
                    </div>
                    <GradientColorPicker
                      defaultFrom={currentGradients.deviceBackground?.from}
                      defaultTo={currentGradients.deviceBackground?.to}
                      onChange={(val) =>
                        handleGradientChange("deviceBackground", val)
                      }
                    />
                  </div>
                </div>

                <div className="flex gap-x-8 mb-5 ">
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 mb-2">
                      <label className="text-sm font-medium">
                        User Background
                      </label>
                      <InfoIcon />
                    </div>
                    <div className="flex text-xs text-gray-400 mb-1">
                      <span className="flex-1">Color</span>
                      <span className="w-[80px]">Transparency</span>
                    </div>
                    <GradientColorPicker
                      defaultFrom={currentGradients.userBackground?.from}
                      defaultTo={currentGradients.userBackground?.to}
                      onChange={(val) =>
                        handleGradientChange("userBackground", val)
                      }
                    />
                  </div>

                  <div className="flex-1" />
                  <div className="flex-1" />
                </div>

                <div className="flex gap-x-8 ">
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 ">
                      <label className="text-sm font-medium">
                        Cards Stroke
                      </label>
                      <InfoIcon />
                    </div>
                    <div className="flex text-xs text-gray-400 mb-1">
                      <span className="flex-1">Color</span>
                    </div>
                    <ColorPicker
                      value={currentStrokes.card ?? "#C6D92C"}
                      label=""
                      onChange={(val) => {
                        handleStrokeChange("card", val);
                      }}
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 mb-2">
                      <label className="text-sm font-medium">
                        Popup bar Stroke
                      </label>
                      <InfoIcon />
                    </div>
                    <div className="flex text-xs text-gray-400 mb-1">
                      <span className="flex-1">Color</span>
                    </div>
                    <ColorPicker
                      value={currentStrokes?.popup ?? "#C6D92C"}
                      label=""
                      onChange={(val) => {
                        handleStrokeChange("popup", val);
                      }}
                    />
                  </div>

                  <div className="flex-1">
                    <div className="flex items-center gap-1.5 mb-2">
                      <label className="text-sm font-medium">
                        Progress bar Stroke
                      </label>
                      <InfoIcon />
                    </div>
                    <div className="flex text-xs text-gray-400 mb-1">
                      <span className="flex-1">Color</span>
                    </div>
                    <ColorPicker
                      value={currentStrokes?.progressBar ?? "#C6D92C"}
                      label=""
                      onChange={(val) => {
                        handleStrokeChange("progressBar", val);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold">
                TV and Application Banner
              </h2>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="text-gray-400"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    TV Banner Image
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Max file size is 5mb. Supported files are JPEG and PNG
                  </p>
                  <input
                    type="file"
                    ref={tvBannerInputRef}
                    className="hidden"
                    accept="image/jpeg, image/png"
                    onChange={handleTvBannerUpload}
                  />
                  <div
                    onClick={() => tvBannerInputRef.current?.click()}
                    className="border-2 border-dashed border-[#B6CB24]/50 rounded-xl p-6 flex flex-col items-center justify-center text-center cursor-pointer hover:bg-[#B6CB24]/5 transition-colors group"
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#C6D92C"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="mb-2"
                    >
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="17 8 12 3 7 8"></polyline>
                      <line x1="12" y1="3" x2="12" y2="15"></line>
                    </svg>
                    <p className="text-sm text-gray-300">
                      Drag and drop file here
                    </p>
                    <p className="text-xs text-gray-500 my-1">or</p>
                    <p className="text-sm text-gray-300">
                      Click to browse files
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Current TV Banner
                  </label>
                  <div className="w-full aspect-[21/9] bg-[#222] border border-[#333] rounded-lg overflow-hidden flex items-center justify-center relative">
                    {tvBannerUrl ? (
                      <img
                        src={tvBannerUrl}
                        alt="TV Banner"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <>
                        <div className="absolute inset-0 bg-gradient-to-tr from-gray-900 to-gray-700 opacity-50"></div>
                        <span className="text-gray-500 text-sm z-10">
                          TV Preview Placeholder
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="xl:col-span-2 flex flex-col sticky top-[100px] self-start z-10">
          <div className="bg-[#1A1A1A] border border-[#2A2A2A] rounded-xl p-6 flex-1 flex flex-col">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-white">Live Preview</h2>
              <div className="flex items-center gap-4">
                <span className="text-xs text-gray-400 font-medium">
                  Changes appear in real-time
                </span>
                <div className="relative">
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400"
                  >
                    <path d="M6 9l6 6 6-6"></path>
                  </svg>
                </div>
              </div>
            </div>

            <hr className="border-t border-[#2A2A2A] -mx-6 mb-6 mt-1" />

            <div className="flex-1 flex flex-col gap-4">
              <ScaledIframe ref={iframeRef} src={livePreviewUrl} />

              <div className="flex justify-center pt-2">
                <button
                  className="flex items-center gap-2 px-6 py-2.5 border border-[#333] bg-[#222] hover:bg-[#2A2A2A] rounded-lg text-sm font-medium transition-colors text-white shadow-sm"
                  onClick={() => iframeRef.current?.enterFullscreen()}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  Preview Full Screen
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showSaveModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: "#1a1a1a",
              border: "1px solid #333",
              borderRadius: "16px",
              padding: "32px",
              width: "420px",
              textAlign: "center",
              position: "relative",
            }}
          >
            <button
              onClick={() => setShowSaveModal(false)}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                background: "transparent",
                border: "none",
                color: "#aaa",
                cursor: "pointer",
                fontSize: "18px",
              }}
            >
              ✕
            </button>

            <div
              style={{
                width: "48px",
                height: "48px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 16px",
              }}
            >
              <svg
                width="56"
                height="56"
                viewBox="0 0 56 56"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect
                  x="4"
                  y="4"
                  width="48"
                  height="48"
                  rx="24"
                  fill="url(#paint0_linear_5894_4526)"
                />
                <rect
                  x="4"
                  y="4"
                  width="48"
                  height="48"
                  rx="24"
                  stroke="url(#paint1_linear_5894_4526)"
                  stroke-width="8"
                />
                <path
                  d="M25.9668 36.3333H18.6667C18.1152 36.3333 17.6667 35.8848 17.6667 35.3333V20.3333H27.6667C27.8509 20.3333 28 20.1841 28 20C28 19.8159 27.8509 19.6667 27.6667 19.6667H17.6667V18.6667C17.6667 18.1152 18.1152 17.6667 18.6667 17.6667H33.3333C33.8848 17.6667 34.3333 18.1152 34.3333 18.6667V24.8167C34.3333 25.0008 34.4824 25.1501 34.6667 25.1501C34.8509 25.1501 35 25.0008 35 24.8167V18.6667C35 17.7477 34.2523 17 33.3333 17H18.6667C17.7477 17 17 17.7477 17 18.6667V35.3333C17 36.2523 17.7477 37 18.6667 37H25.9668C26.151 37 26.3001 36.8507 26.3001 36.6667C26.3001 36.4826 26.151 36.3333 25.9668 36.3333Z"
                  fill="white"
                />
                <path
                  d="M18.9998 18.3333C18.8156 18.3333 18.6665 18.4826 18.6665 18.6667C18.6665 18.8508 18.8156 19 18.9998 19H19.3332C19.5174 19 19.6665 18.8508 19.6665 18.6667C19.6665 18.4826 19.5174 18.3333 19.3332 18.3333H18.9998Z"
                  fill="white"
                />
                <path
                  d="M20.6668 18.3333C20.4826 18.3333 20.3335 18.4826 20.3335 18.6667C20.3335 18.8508 20.4826 19 20.6668 19H21.0002C21.1844 19 21.3335 18.8508 21.3335 18.6667C21.3335 18.4826 21.1844 18.3333 21.0002 18.3333H20.6668Z"
                  fill="white"
                />
                <path
                  d="M22.3333 18.3333C22.1491 18.3333 22 18.4826 22 18.6667C22 18.8508 22.1491 19 22.3333 19H22.6667C22.8509 19 23 18.8508 23 18.6667C23 18.4826 22.8509 18.3333 22.6667 18.3333H22.3333Z"
                  fill="white"
                />
                <path
                  d="M31.3332 20.3333C31.5174 20.3333 31.6665 20.1841 31.6665 20C31.6665 19.8159 31.5174 19.6667 31.3332 19.6667H28.9998C28.8156 19.6667 28.6665 19.8159 28.6665 20C28.6665 20.1841 28.8156 20.3333 28.9998 20.3333H31.3332Z"
                  fill="white"
                />
                <path
                  d="M33.3333 20C33.3333 19.8159 33.1842 19.6667 33 19.6667H32.3333C32.1491 19.6667 32 19.8159 32 20C32 20.1841 32.1491 20.3333 32.3333 20.3333H33C33.1842 20.3333 33.3333 20.1841 33.3333 20Z"
                  fill="white"
                />
                <path
                  d="M25 26.6667V22C25 21.8159 24.8509 21.6667 24.6667 21.6667H19.3333C19.1491 21.6667 19 21.8159 19 22V26.6667C19 26.8507 19.1491 27 19.3333 27H24.6667C24.8509 27 25 26.8507 25 26.6667ZM24.3333 26.3333H19.6667V22.3333H24.3333V26.3333Z"
                  fill="#C6D92C"
                />
                <path
                  d="M19.3333 27.6667C19.1491 27.6667 19 27.8159 19 28V34.6667C19 34.8507 19.1491 35 19.3333 35H24C24.1842 35 24.3333 34.8507 24.3333 34.6667V28C24.3333 27.8159 24.1842 27.6667 24 27.6667H19.3333ZM23.6667 34.3333H19.6667V28.3333H23.6667V34.3333Z"
                  fill="white"
                />
                <path
                  d="M32.6665 24.6966C32.8507 24.6966 32.9998 24.5474 32.9998 24.3633V22C32.9998 21.8159 32.8507 21.6667 32.6665 21.6667H25.9998C25.8156 21.6667 25.6665 21.8159 25.6665 22V26.6667C25.6665 26.8507 25.8156 27 25.9998 27H26.4933C26.6776 27 26.8267 26.8507 26.8267 26.6667C26.8267 26.4826 26.6776 26.3333 26.4933 26.3333H26.3332V22.3333H32.3332V24.3633C32.3332 24.5474 32.4823 24.6966 32.6665 24.6966Z"
                  fill="white"
                />
                <path
                  d="M32.4653 25.015C30.4897 24.8885 28.6125 25.5638 27.1828 26.9202C25.7729 28.2575 24.9777 30.1398 25.0005 32.0843C25.0457 35.8976 28.2622 39 32.1707 39C32.812 39 33.3335 38.4783 33.3335 37.8372V34.3333C33.3335 33.7819 33.782 33.3333 34.3335 33.3333H37.8374C38.4787 33.3333 39.0001 32.8117 39.0001 32.1706C39.0001 28.395 36.1297 25.2518 32.4653 25.015ZM37.8374 32.6667H34.3335C33.4145 32.6667 32.6668 33.4144 32.6668 34.3333V37.8372C32.6668 38.1108 32.4442 38.3333 32.1707 38.3333C28.6255 38.3333 25.7082 35.5265 25.6671 32.0765C25.6463 30.3169 26.366 28.6138 27.6414 27.404C28.8319 26.2749 30.3641 25.6665 31.9953 25.6665C32.1369 25.6665 32.2794 25.671 32.4224 25.6803C35.7371 25.8945 38.3335 28.7454 38.3335 32.1706C38.3335 32.4442 38.1108 32.6667 37.8374 32.6667Z"
                  fill="white"
                />
                <path
                  d="M29.3332 32C29.3332 31.2648 28.7352 30.6667 27.9998 30.6667C27.2645 30.6667 26.6665 31.2648 26.6665 32C26.6665 32.7352 27.2645 33.3333 27.9998 33.3333C28.7352 33.3333 29.3332 32.7352 29.3332 32ZM27.9998 32.6667C27.6323 32.6667 27.3332 32.3677 27.3332 32C27.3332 31.6323 27.6323 31.3333 27.9998 31.3333C28.3674 31.3333 28.6665 31.6323 28.6665 32C28.6665 32.3677 28.3674 32.6667 27.9998 32.6667Z"
                  fill="#C6D92C"
                />
                <path
                  d="M30.6666 34.3094C30.3583 34.1314 29.9989 34.0845 29.6549 34.1763C29.3108 34.2684 29.0234 34.4889 28.8453 34.7972C28.6672 35.1058 28.6197 35.4652 28.7122 35.8093C28.8043 36.1532 29.025 36.4408 29.3333 36.6187C29.5387 36.7373 29.7665 36.798 29.9976 36.798C30.1135 36.798 30.2301 36.7827 30.345 36.752C30.689 36.6598 30.9765 36.4391 31.1545 36.1309C31.3326 35.8224 31.3801 35.4631 31.2877 35.119C31.1956 34.7751 30.9749 34.4875 30.6666 34.3094ZM30.5771 35.7974C30.4882 35.9517 30.3443 36.062 30.1724 36.1081C29.9989 36.1537 29.8206 36.1302 29.6666 36.0414C29.5123 35.9523 29.4019 35.8086 29.356 35.6367C29.3098 35.4647 29.3336 35.285 29.4228 35.1307C29.5116 34.9766 29.6555 34.8662 29.8274 34.8202C29.885 34.8049 29.9433 34.7972 30.0009 34.7972C30.1165 34.7972 30.2304 34.8275 30.3333 34.8867C30.4876 34.9758 30.5979 35.1196 30.6438 35.2915C30.69 35.4635 30.6663 35.6431 30.5771 35.7974Z"
                  fill="white"
                />
                <path
                  d="M34.6666 27.3813C34.3583 27.2033 33.9989 27.1564 33.6549 27.248C33.3108 27.3402 33.0234 27.5609 32.8453 27.8691C32.6672 28.1776 32.6197 28.5369 32.7122 28.881C32.8043 29.2249 33.025 29.5125 33.3333 29.6906C33.5387 29.8092 33.7668 29.8698 33.9976 29.8698C34.1135 29.8698 34.2301 29.8545 34.345 29.8237C34.689 29.7316 34.9765 29.5111 35.1545 29.2028C35.3326 28.8942 35.3801 28.5348 35.2877 28.1907C35.1956 27.8468 34.9749 27.5592 34.6666 27.3813ZM34.5771 28.8693C34.4882 29.0234 34.3443 29.1338 34.1724 29.1798C33.9993 29.2256 33.8206 29.2021 33.6666 29.1133C33.5123 29.0242 33.4019 28.8804 33.356 28.7085C33.3098 28.5364 33.3336 28.3569 33.4228 28.2026C33.5116 28.0483 33.6555 27.938 33.8274 27.8919C33.885 27.8766 33.9433 27.869 34.0009 27.869C34.1165 27.869 34.2304 27.8992 34.3333 27.9586C34.4876 28.0477 34.5979 28.1914 34.6438 28.3633C34.69 28.5353 34.6663 28.715 34.5771 28.8693Z"
                  fill="#C6D92C"
                />
                <path
                  d="M30.345 27.248C30.0009 27.1561 29.6415 27.203 29.3332 27.3812C29.025 27.5592 28.8043 27.8468 28.7122 28.1908C28.6197 28.5348 28.6672 28.8942 28.8453 29.2026C29.0234 29.5111 29.3108 29.7316 29.6549 29.8237C29.7698 29.8545 29.8863 29.8698 30.0022 29.8698C30.233 29.8698 30.4612 29.8092 30.6666 29.6906C30.9748 29.5125 31.1955 29.2249 31.2877 28.881C31.3801 28.5369 31.3326 28.1776 31.1545 27.8693C30.9765 27.5609 30.689 27.3402 30.345 27.248ZM30.6438 28.7085C30.5979 28.8804 30.4875 29.0241 30.3332 29.1131C30.179 29.202 29.9996 29.2257 29.8274 29.1799C29.6555 29.1338 29.5116 29.0234 29.4228 28.8691C29.3336 28.715 29.3098 28.5353 29.356 28.3633C29.4019 28.1914 29.5123 28.0477 29.6666 27.9585C29.7694 27.8993 29.8834 27.869 29.9989 27.869C30.0566 27.869 30.1148 27.8766 30.1724 27.8919C30.3443 27.938 30.4882 28.0483 30.5771 28.2028C30.6663 28.3569 30.69 28.5365 30.6438 28.7085Z"
                  fill="white"
                />
                <defs>
                  <linearGradient
                    id="paint0_linear_5894_4526"
                    x1="28"
                    y1="4"
                    x2="28"
                    y2="52"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stop-color="#1F1F1F" />
                    <stop offset="1" stop-color="#2E2E2E" />
                  </linearGradient>
                  <linearGradient
                    id="paint1_linear_5894_4526"
                    x1="28"
                    y1="4"
                    x2="28"
                    y2="52"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stop-color="#2E2E2E" />
                    <stop offset="1" stop-color="#1F1F1F" />
                  </linearGradient>
                </defs>
              </svg>
            </div>

            <h2
              style={{
                color: "#C6D92C",
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "20px",
              }}
            >
              Enter Theme Name
            </h2>

            <input
              type="text"
              placeholder="Enter here"
              value={themeName}
              onChange={(e) => setThemeName(e.target.value)}
              style={{
                width: "100%",
                background: "#111",
                border: "1px solid #444",
                borderRadius: "8px",
                padding: "10px 14px",
                color: "white",
                fontSize: "14px",
                outline: "none",
                marginBottom: "16px",
                boxSizing: "border-box",
              }}
            />

            <div style={{ display: "flex", gap: "12px" }}>
              <button
                onClick={handleSaveTheme}
                disabled={!themeName.trim() || isSavingTheme}
                style={{
                  flex: 1,
                  background:
                    "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 0.80) 0%, rgba(83, 91, 16, 0.80) 100%)",
                  border: "1px solid var(--menu-active-border, #C6D92C)",
                  borderRadius: "10px",
                  padding: "10px",
                  color: "white",
                  fontWeight: "600",
                  cursor: !themeName.trim() || isSavingTheme ? "not-allowed" : "pointer",
                  fontSize: "14px",
                }}
              >
                {isSavingTheme ? "Saving..." : "Save"}
              </button>
              <button
                onClick={() => setShowSaveModal(false)}
                style={{
                  flex: 1,
                  background:
                    "radial-gradient(94.17% 100% at 52.48% 0%, rgba(46, 46, 46, 0.8) 0%, rgba(71, 71, 71, 0.8) 100%)",
                  border: "1px solid var(--menu-active-border, #9B9DB6)",
                  borderRadius: "10px",
                  padding: "10px",
                  color: "white",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Back
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateTheme;
